import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Types
import { Member, OrganizationRoleType } from 'Shared/types/Member.type';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Hooks
import { useMembers, useToggle, useQueryParam } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Actions
import { updateMemberAction, destroyMemberAction } from 'Shared/actions/members';
import { getBillingDataAction } from 'Shared/actions/organizations';

// Components
import WpButton from 'Wpds/components/button/WpButton';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpAlert from 'Wpds/components/alert/WpAlert';
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import { WpDataTableV2, SortDirections } from 'Wpds/components/table-v2';
import MemberName from './partials/MemberName';
import MemberLastSeenAt from './partials/MemberLastSeenAt';
import MemberActions from './partials/MemberActions';
import MemberRole from './partials/MemberRole';
import ManageMemberModal from './ManageMemberModal';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';
import MessageModal from 'Shared/components/MessageModal/MessageModal';

// Styles
import './MembersTable.scss';
import { WpLink } from 'Wpds/components/link';

const MembersTable = () => {
  const dispatch = useDispatch();
  const { isLoading: isLoadingMembers } = useMembers({ refresh: true });
  const { isThirdPartyUser } = useCurrentUser();
  const [textSearchInput, setTextSearchInput] = useState('');
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const [isCannotRemoveAdminModalOpen, toggleIsCannotRemoveAdminModalOpen] = useToggle(false);
  const [isCannotRemoveUserModalOpen, toggleIsCannotRemoveUserModalOpen] = useToggle(false);
  const [isRemoveModalOpen, toggleIsRemoveModalOpen] = useToggle(false);
  const [isRemovingUser, toggleIsRemovingUser] = useToggle(false);
  const [editingMember, setEditingMember] = useState(null);
  const [, setAddUserParam] = useQueryParam(QueryParamConstants.ADD_USER_MODAL);

  const members = useSelector(state => state.members);

  const onRowClick = member => {
    setEditingMember(member);
    toggleIsModalOpen();
  };

  const handleRemoveUser = () => {
    toggleIsRemovingUser();
    dispatch(destroyMemberAction(editingMember.memberId)).then(() => {
      toggleIsRemovingUser();
      toggleIsRemoveModalOpen();
      dispatch(getBillingDataAction());
      if (isModalOpen) toggleIsModalOpen();
    });
  };

  const handleRemoveClick = useCallback(
    member => {
      setEditingMember(member);

      const isOnlyOneAdmin =
        members.filter(member => member.organizationRole === OrganizationRoleType.admin).length === 1;

      const onlyAdmin = members.find(member => member.organizationRole === OrganizationRoleType.admin);

      if (isOnlyOneAdmin && onlyAdmin.userId === member.userId) {
        toggleIsCannotRemoveUserModalOpen();
      } else {
        toggleIsRemoveModalOpen();
      }
    },
    [members]
  );

  const handleChangeRole = useCallback(
    member => {
      const newRole =
        member.organizationRole === OrganizationRoleType.admin
          ? OrganizationRoleType.regular_user
          : OrganizationRoleType.admin;

      const isOnlyOneAdmin =
        members.filter(member => member.organizationRole === OrganizationRoleType.admin).length === 1;

      const onlyAdmin = members.find(member => member.organizationRole === OrganizationRoleType.admin);

      if (isOnlyOneAdmin && onlyAdmin.userId === member.userId && newRole === OrganizationRoleType.regular_user) {
        toggleIsCannotRemoveAdminModalOpen();
      } else {
        dispatch(
          updateMemberAction({
            id: member.memberId,
            organizationRole: newRole,
          })
        );
      }
    },
    [members]
  );

  const addDepartmentColumn = () => {
    const membersWithDeptSet = members.filter(member => Boolean(member?.department));

    if (membersWithDeptSet.length > 0) {
      return [
        {
          label: 'Department',
          selector: row => row.department,
          isSortable: false,
        },
      ];
    }
    return [];
  };

  const handleAddUserClick = () => setAddUserParam(true);

  const columns = [
    {
      label: 'Name',
      selector: row => row.fullName,
      component: ({ row }) => <MemberName member={row} />,
      maxWidth: '25rem',
      sort: SortDirections.ASC,
    },
    {
      label: 'Manager',
      selector: (row: Member) => members.find(member => row.managerId === member.id)?.fullName || '--',
    },
    ...addDepartmentColumn(),
    {
      label: 'Role',
      selector: row => row.role,
      component: ({ row }) => <MemberRole member={row} />,
      isSortable: false,
    },
    {
      label: 'Last Seen',
      selector: row => row.lastSeenAt,
      component: ({ row }) => <MemberLastSeenAt member={row} />,
      isSortable: false,
    },
    {
      label: 'Origin',
      selector: row => row.membershipOrigin,
      isSortable: false,
    },
    {
      label: 'Actions',
      rightAlign: true,
      hideLabel: true,
      selector: row => row.lastSeenAt,
      component: ({ row }) => (
        <MemberActions
          member={row}
          handleEditMember={onRowClick}
          handleRemoveUser={handleRemoveClick}
          handleChangeRole={handleChangeRole}
        />
      ),
      isSortable: false,
    },
  ];

  const membersToRender = members.filter(
    member =>
      member.fullName.toLocaleLowerCase().includes(textSearchInput.toLocaleLowerCase()) ||
      member.email.toLocaleLowerCase().includes(textSearchInput.toLocaleLowerCase())
  );

  const showLoading = isLoadingMembers && members.length === 0;
  const baseClassName = 'MembersTable';
  const workspaceHelpUrl =
    'https://docs.predictiveindex.com/perform/admin-guide-perform/workplace-management/transferring-workspace-history/';

  return (
    <>
      <WpContainer>
        <WpContainerHeader>
          <h5>Members</h5>
        </WpContainerHeader>
        <WpContainerBody>
          <div className={`${baseClassName}__toolbar`}>
            <div className={`${baseClassName}__toolbar__search`}>
              <WpTextField
                id="members-search-input"
                label="Search Members"
                placeholder="Search Members"
                labelHidden
                prefixIcon="search"
                value={textSearchInput}
                onChange={setTextSearchInput}
              />
            </div>
            {!isThirdPartyUser && <WpButton onClick={handleAddUserClick}>Add member</WpButton>}
          </div>
        </WpContainerBody>
        <WpDataTableV2
          tableId="membersTable"
          data={membersToRender}
          columns={columns}
          showInContainer={false}
          rowClick={onRowClick}
          isLoading={showLoading}
        />
      </WpContainer>
      {editingMember && (
        <ManageMemberModal
          isOpen={isModalOpen}
          toggleOpen={toggleIsModalOpen}
          member={editingMember}
          handleRemoveUser={handleRemoveClick}
        />
      )}
      {isRemoveModalOpen && (
        <ConfirmModal
          title="Remove User"
          isOpen={isRemoveModalOpen}
          isProcessing={isRemovingUser}
          onToggle={toggleIsRemoveModalOpen}
          onConfirmClick={handleRemoveUser}
          confirmButtonLabel="Remove"
        >
          <div>
            <p>Are you sure you want to remove {editingMember.fullName}?</p>
            <WpAlert type="note" showIcon={false}>
              Heads up, when you remove a user:
              <ul className={`${baseClassName}__remove__list`}>
                <li>All their 1:1 workspaces are archived </li>
                <li>The goals associated with their 1:1 workspaces will be archived</li>
                <li>Their action items in group workspaces will remain, but will be unassigned</li>
              </ul>
            </WpAlert>
            <p className={`${baseClassName}__remove__transfer`}>
              If you would like to transfer any data from them, please do so before removing this person.&nbsp;&nbsp;
              <WpLink href={workspaceHelpUrl} target="_blank" rel="noopener noreferrer">
                Learn how to transfer.
              </WpLink>
            </p>
          </div>
        </ConfirmModal>
      )}
      {isCannotRemoveAdminModalOpen && (
        <MessageModal
          isOpen={isCannotRemoveAdminModalOpen}
          onToggle={toggleIsCannotRemoveAdminModalOpen}
          title="Remove admin access"
          message="You must add another admin to the organization before removing your admin access."
        />
      )}
      {isCannotRemoveUserModalOpen && (
        <MessageModal
          isOpen={isCannotRemoveUserModalOpen}
          onToggle={toggleIsCannotRemoveUserModalOpen}
          title="Remove user"
          message="You must add another admin to the organization before removing this user."
        />
      )}
    </>
  );
};

export default MembersTable;
